import { Link, useParams, useRouterState } from "@tanstack/react-router";
import clsx from "clsx";
import { Check, ChevronsUpDown, FolderKanban, LayoutList, Plus, Search } from "lucide-react";
import { Popover, Tooltip } from "radix-ui";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useProjects } from "../api/queries.js";
import { useIsAdmin } from "../api/users.js";
import { lastProjectId } from "./last-project.js";
import { activeItem, NAV, type NavLink, type NavSection } from "./nav.js";

/*
 * The project switcher at the top of the left block (docs/web.md, "Menu"):
 * the project in view, and a popover with a search, every project, «Все
 * проекты» and «+ Новый проект» (administrators). Switching keeps the
 * section: from a project's firewalls to the other project's firewalls.
 */

/**
 * The project the menu shows: the one in the address; on pages outside a
 * project the last one opened in this browser (while it still exists), or
 * the only one there is.
 */
export function useNavProject(): string | undefined {
  const params = useParams({ strict: false });
  const projects = useProjects();
  const fromUrl = params.projectId;
  if (fromUrl) return fromUrl;
  const list = projects.data ?? [];
  // The router remembers it on every project page (`useRememberProject`).
  const last = lastProjectId();
  if (last && list.some((p) => p.id === last)) return last;
  return list.length === 1 ? list[0]?.id : undefined;
}

const PROJECT_PREFIX = "/projects/$projectId";

/** The route of the item in view when it is a project's, so switching keeps the section. */
function sameSection(path: string): string {
  const active = activeItem(path);
  if (!active) return PROJECT_PREFIX;
  for (const group of NAV)
    for (const section of group.items) {
      if (section.id !== active.section) continue;
      const item: NavSection | NavLink | undefined = active.link
        ? section.children?.find((c) => c.id === active.link)
        : section;
      const to = item?.to;
      if (to?.startsWith(PROJECT_PREFIX)) return to;
    }
  return PROJECT_PREFIX;
}

const ROW =
  "flex min-h-9 w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-sm outline-none hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:ring-2 focus-visible:ring-accent";

export function ProjectSwitcher({
  projectId,
  compact = false,
  onPick,
}: {
  /** The project the menu shows (`useNavProject`). */
  projectId: string | undefined;
  /** The icon rail: an icon button with a tooltip, the popover to the right. */
  compact?: boolean;
  /** Called after a choice (the phone sheet closes). */
  onPick?: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  const projects = useProjects();
  const admin = useIsAdmin();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchId = useId();
  const list = projects.data ?? [];
  const current = list.find((p) => p.id === projectId);
  const name = current?.name ?? t("nav.projects");
  const needle = query.trim().toLocaleLowerCase();
  const shown = needle ? list.filter((p) => p.name.toLocaleLowerCase().includes(needle)) : list;
  const target = sameSection(path);
  const pick = () => {
    setOpen(false);
    setQuery("");
    onPick?.();
  };

  const trigger = compact ? (
    <button
      type="button"
      aria-label={`${t("nav.switcher.caption")}: ${name}`}
      className="flex size-10 items-center justify-center rounded-lg border border-line bg-surface text-sm font-semibold text-accent-strong hover:bg-surface-2"
    >
      {current ? (
        current.name.trim().charAt(0).toUpperCase()
      ) : (
        <FolderKanban className="size-4" aria-hidden />
      )}
    </button>
  ) : (
    <button
      type="button"
      title={name}
      aria-label={`${t("nav.switcher.caption")}: ${name}`}
      className="flex w-full min-w-0 items-center gap-2 rounded-lg border border-line bg-surface px-2.5 py-1.5 text-left hover:bg-surface-2"
    >
      <FolderKanban className="size-4 shrink-0 text-muted" aria-hidden />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-[0.7rem] leading-tight text-muted">{t("nav.switcher.caption")}</span>
        <span className="truncate text-sm font-medium leading-tight">{name}</span>
      </span>
      <ChevronsUpDown className="size-4 shrink-0 text-muted" aria-hidden />
    </button>
  );

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
    >
      {compact ? (
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <Popover.Trigger asChild>{trigger}</Popover.Trigger>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content
              side="right"
              sideOffset={8}
              className="z-50 rounded-md bg-text px-2 py-1 text-xs text-surface shadow"
            >
              {`${t("nav.switcher.caption")}: ${name}`}
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      ) : (
        <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      )}
      <Popover.Portal>
        <Popover.Content
          aria-label={t("nav.switcher.title")}
          side={compact ? "right" : "bottom"}
          align="start"
          sideOffset={6}
          collisionPadding={8}
          className="z-50 flex w-72 max-w-[calc(100vw-1rem)] flex-col gap-1 rounded-xl border border-line bg-surface p-1.5 shadow-lg"
        >
          <label htmlFor={searchId} className="sr-only">
            {t("nav.switcher.search")}
          </label>
          <span className="relative flex items-center">
            <Search
              className="pointer-events-none absolute left-2.5 size-4 text-muted"
              aria-hidden
            />
            <input
              id={searchId}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("nav.switcher.search")}
              className="min-h-9 w-full rounded-lg border border-line bg-surface pl-8 pr-2.5 text-sm"
            />
          </span>
          <ul aria-label={t("nav.switcher.list")} className="max-h-72 overflow-y-auto">
            {shown.map((project) => (
              <li key={project.id}>
                <Link
                  to={target as "/projects/$projectId"}
                  params={{ projectId: project.id }}
                  onClick={pick}
                  aria-current={project.id === projectId ? "true" : undefined}
                  title={project.name}
                  className={clsx(ROW, project.id === projectId && "font-medium")}
                >
                  <span className="min-w-0 flex-1 truncate">{project.name}</span>
                  {project.id === projectId && (
                    <Check className="size-4 shrink-0 text-accent" aria-hidden />
                  )}
                </Link>
              </li>
            ))}
            {shown.length === 0 && (
              <li className="px-2.5 py-2 text-sm text-muted">
                {list.length === 0 ? t("nav.switcher.empty") : t("nav.switcher.none")}
              </li>
            )}
          </ul>
          <div className="flex flex-col border-t border-line pt-1">
            <Link to="/" onClick={pick} className={ROW}>
              <LayoutList className="size-4 text-muted" aria-hidden />
              {t("nav.projects")}
            </Link>
            {admin && (
              <Link to="/" search={{ connect: true }} onClick={pick} className={ROW}>
                <Plus className="size-4 text-muted" aria-hidden />
                {t("nav.switcher.new")}
              </Link>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
